import {Icon} from '@atoms';
import {useModal} from '@hooks';
import {
  getAreaById,
  getIsLastNodeSelected,
  getIsLastSelectedWorkspaceReservationLocationLoaded,
  getIsLoadingDeskSuggestions,
  getIsSelectedBuildingWithoutFloors,
  getLastSelectedWorkspaceReservationLocation,
  getNodeTypeAndId,
  getSelectedBuildingName,
  getSelectedNodes,
  getSelectedSectionSubtitle,
  getSelectedSectionTitle,
  selectArea,
  selectDesks,
  selectFloor,
  selectLocationBasedOnBuildingId,
  useAppDispatch,
  useAppSelector,
} from '@lib/store';
import {OfficeWorkday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {dateKeyForDateTimeString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {getSortedConnectedUsersWithOfficeDayOnDate} from '@lib/store/src/data/timeslots/selectors';
import {AvatarStack, ModalCell, ModalCellButton, ModalCellItem, ModalCellItemIcon} from '@molecules';
import {WorkspaceSelectionCard} from '@organisms';
import {FlexCol, P, Span} from '@quarks';
import {pxToRem} from '@utils';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useTheme} from 'styled-components';
import {Member} from '../BookingDetailsCard/timeslots/useReservationDetails';
import {StyledText} from '../BookWorkspaceCard/styles';

// TODO: move to selector in libs
// TODO: closed / capacity 0 / full checks
// This is built to mimic the `getInitialSelectedWorkspace` selector
const useInitialWorkspaceSelection = (
  workday: OfficeWorkday,
  workspaceReservation?: WorkspaceReservation,
  members: Array<Member> = [],
) => {
  return useAppSelector((state) => {
    // If editing a reservation, return that reservation's locations
    if (workspaceReservation) {
      const {workspace} = workspaceReservation;
      return {
        buildingId: workspace.buildingId,
        // TODO: defaulting to empty string hurts, but I'm going for backwards compatabiltiy here...
        floorId: 'floorId' in workspace ? workspace.floorId : '',
        areaId: 'areaId' in workspace ? workspace.areaId : '',
        deskIds: members.flatMap((m) =>
          m.reservation.workspace.nodeType === 'Desk' ? [m.reservation.workspace.deskId] : [],
        ),
      };
    }

    // If last selection from local storage is in workday building, use that
    const lastUsed = getLastSelectedWorkspaceReservationLocation(state);
    if (lastUsed.buildingId === workday.buildingId) {
      return {
        buildingId: lastUsed.buildingId,
        floorId: lastUsed.floorId,
        areaId: lastUsed.areaId,
        deskIds: lastUsed.deskId ? [lastUsed.deskId] : [],
      };
    }

    // if no smart suggestions can be resolved (e.g. first use of app), return just workday's building id
    return {
      buildingId: workday.buildingId,
    };
  });
};

export function SelectWorkspaceButton_timeslots({
  workday,
  workspaceReservation,
  members = [],
  inviteeCount,
}: {
  workday: OfficeWorkday;
  workspaceReservation?: WorkspaceReservation;
  members?: Array<Member>;
  inviteeCount: number;
}) {
  const date = dateKeyForDateTimeString(workday.localStart);
  const {addPages} = useModal();
  const theme = useTheme();
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const {buildingId, floorId, areaId, deskIds} = useAppSelector(getSelectedNodes);
  const lastSelectedWorkspaceLoaded = useAppSelector(getIsLastSelectedWorkspaceReservationLocationLoaded);

  const initialSelectedWorkspace = useInitialWorkspaceSelection(workday, workspaceReservation, members);

  const {
    buildingId: initialBuildingId,
    floorId: initialFloorId,
    areaId: initialAreaId,
    deskIds: initialDeskIds = [],
  } = initialSelectedWorkspace ?? {};

  const {
    nodeIds: [selectedNodeId],
    nodeType,
  } = getNodeTypeAndId(buildingId, floorId, areaId, deskIds);

  useEffect(
    function selectInitial() {
      if (initialSelectedWorkspace && lastSelectedWorkspaceLoaded) {
        if (!buildingId) {
          dispatch(selectLocationBasedOnBuildingId(initialBuildingId ?? ''));

          if (initialFloorId) {
            dispatch(selectFloor(initialFloorId ?? ''));
            dispatch(selectArea(initialAreaId ?? ''));
            dispatch(selectDesks(initialDeskIds ?? []));
          }
        }

        if (buildingId && buildingId === initialBuildingId && !floorId && initialFloorId) {
          dispatch(selectFloor(initialFloorId ?? ''));
          dispatch(selectArea(initialAreaId ?? ''));
          dispatch(selectDesks(initialDeskIds ?? []));
        }
      }
    },
    [
      buildingId,
      areaId,
      dispatch,
      initialAreaId,
      initialBuildingId,
      initialDeskIds,
      initialFloorId,
      initialSelectedWorkspace,
      lastSelectedWorkspaceLoaded,
      floorId,
    ],
  );

  const nodeId = areaId ?? floorId ?? buildingId;
  const selectedArea = useAppSelector((state) => getAreaById(state, selectedNodeId));

  // TODO: This selector should handle buildings that support direct bookings without floors
  const defaultTitle = useAppSelector((state) => getSelectedSectionTitle(state, date));
  const fallbackTitle = useAppSelector(getSelectedBuildingName);
  const selectedNodeTitle = defaultTitle || fallbackTitle;

  // TODO: check capacity of selection
  const hasInsufficientSpaceForGroupBooking = false;

  const selectedNodeSubTitle = useAppSelector((state) => {
    if (hasInsufficientSpaceForGroupBooking) {
      return t('workplace:NotEnoughAvailableDesks');
    }

    const deskSubTitle = t('workplace:AnyAvailableDesk', {count: inviteeCount || 1});

    // TODO: this logic should handle building level bookings (now returns void)
    return getSelectedSectionSubtitle(state, deskSubTitle) || deskSubTitle;
  });

  const hasBookableSelection = useAppSelector(getIsLastNodeSelected);

  const selectedBuildingIsWithoutFloors = useAppSelector((state) => getIsSelectedBuildingWithoutFloors(state));

  // TODO: add to selector
  const isDisabled = useAppSelector(getIsLoadingDeskSuggestions);
  const locationCanBeChanged = !selectedBuildingIsWithoutFloors;

  const connections = useAppSelector((state) => getSortedConnectedUsersWithOfficeDayOnDate(state, date));

  if (!hasBookableSelection) {
    return (
      <ModalCellButton
        data-testid="select-workspace-button"
        onClick={() =>
          addPages([
            <WorkspaceSelectionCard
              date={date}
              inviteeCount={inviteeCount}
            />,
          ])
        }>
        <P color={theme.input['placeholder']}>{t('workplace:SelectWorkspace')}</P>
      </ModalCellButton>
    );
  }

  return (
    <ModalCell>
      <ModalCellItem
        alignItems="flex-start"
        data-testid="select-workspace-button"
        disabled={isDisabled || !locationCanBeChanged}
        left={
          <ModalCellItemIcon
            icon={'desk'}
            disabled={isDisabled}
          />
        }
        middle={
          <FlexCol
            gap={8}
            paddingTop={8}>
            <StyledText
              $disabled={isDisabled}
              fontWeight="bold">
              {selectedNodeTitle}
            </StyledText>
            <StyledText $disabled={isDisabled}>{selectedNodeSubTitle}</StyledText>
            <AvatarStack
              date={date}
              disabled={isDisabled}
              users={connections}
              tooltipId="SelectWorkspaceButton"
              overlap
            />
          </FlexCol>
        }
        right={
          locationCanBeChanged && (
            <Span padding={4}>
              <Icon
                size={pxToRem(24)}
                icon="arrowRight"
                aria-label={t('workplace:CheckRoomInfo')}
              />
            </Span>
          )
        }
        onClick={() =>
          !isDisabled &&
          locationCanBeChanged &&
          addPages([
            <WorkspaceSelectionCard
              date={date}
              inviteeCount={inviteeCount}
            />,
          ])
        }
      />
    </ModalCell>
  );
}
